import React, { useCallback, useEffect, useState } from "react";

import {
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  ListSubheader,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";

import { RushaIcon } from "@shell/components/icons";
import { PLATFORM_PRODUCTS } from "src/platform/constants/routes";
import { useActiveOrganization } from "src/platform/hooks/useActiveOrganization";
import { useAppNavigation } from "src/platform/hooks/useAppNavigation";
import { useRegistryApi } from "src/platform/hooks/useRegistryApi";

/**
 * Known platform products surfaced in the Apps launcher (S15).
 *
 * Mirrors the product catalog rendered on the Platform Products page. Each
 * entry carries the `productKey` used to route to its details page and the
 * `serviceAlias` used to detect an active org-scoped binding.
 */
interface ProductEntry {
  productKey: string;
  name: string;
  category: string;
  serviceAlias: string;
}

const PRODUCTS: ProductEntry[] = [
  {
    productKey: "llm-gateway",
    name: "LLM Gateway",
    category: "AI infrastructure",
    serviceAlias: "llm-gateway",
  },
  {
    productKey: "customer-identity",
    name: "Customer Identity",
    category: "Application identity",
    serviceAlias: "customer-identity",
  },
];

interface ServiceBinding {
  id: number;
  serviceAlias: string;
  status: string;
}

/**
 * Styled product mark (S15).
 *
 * Active products render a filled blue mark; available-but-inactive products
 * render a neutral outlined mark. This replaces the previous generic cloud
 * icon + "Platform Products" label.
 */
const ProductMark: React.FC<{ active: boolean }> = ({ active }) => {
  const theme = useTheme();
  return (
    <Box
      aria-hidden
      sx={{
        width: 22,
        height: 22,
        borderRadius: "6px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "0.8rem",
        fontWeight: 700,
        lineHeight: 1,
        ...(active
          ? {
              bgcolor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.32)}`,
            }
          : {
              bgcolor: "transparent",
              color: "text.secondary",
              border: `1px solid ${theme.palette.divider}`,
            }),
      }}
    >
      ✦
    </Box>
  );
};

const AppsLauncher: React.FC = () => {
  const theme = useTheme();
  const appNavigation = useAppNavigation();
  const { orgAlias } = useActiveOrganization();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeServices, setActiveServices] = useState<Set<string>>(new Set());
  const open = Boolean(anchorEl);

  const { call: listBindings } = useRegistryApi<ServiceBinding[]>(
    "application",
    "listOrgServiceBindings",
  );

  // Detect active (ready) product bindings so the launcher can mark them.
  const loadActiveServices = useCallback(async () => {
    if (!orgAlias) {
      setActiveServices(new Set());
      return;
    }
    try {
      const result = await listBindings({ pathParams: { alias: orgAlias } });
      if (result && result.length > 0) {
        const ready = new Set(
          result.filter((b) => b.status === "ready").map((b) => b.serviceAlias),
        );
        setActiveServices(ready);
      } else {
        setActiveServices(new Set());
      }
    } catch {
      // Not activated yet / endpoint unavailable — show products as available.
      setActiveServices(new Set());
    }
  }, [orgAlias, listBindings]);

  useEffect(() => {
    void loadActiveServices();
  }, [loadActiveServices]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProduct = (productKey: string) => {
    if (!orgAlias) return;
    handleClose();
    appNavigation.goToPlatformProductDetails(orgAlias, productKey);
  };

  const handlePlatformProducts = () => {
    if (!orgAlias) return;
    handleClose();
    appNavigation.goToPlatformProducts(orgAlias);
  };

  const handleOrganization = () => {
    handleClose();
    appNavigation.goToOrganizations();
  };

  const handleProjects = () => {
    if (!orgAlias) return;
    handleClose();
    appNavigation.goToProjects(orgAlias);
  };

  return (
    <>
      <Tooltip title="Open apps">
        <IconButton
          onClick={handleOpen}
          aria-label="Open apps"
          aria-controls={open ? "apps-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
          sx={{
            width: 34,
            height: 34,
            borderRadius: "8px",
            color: "text.secondary",
            "&:hover": {
              color: "text.primary",
              bgcolor: "action.hover",
            },
            "&:focus-visible": {
              outline: `2px solid ${theme.palette.primary.main}`,
              outlineOffset: 2,
            },
          }}
        >
          <RushaIcon name="apps" size={20} />
        </IconButton>
      </Tooltip>

      <Menu
        id="apps-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{ "aria-label": "Apps" }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            mt: 1,
            width: 286,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1.5,
            boxShadow: theme.shadows[4],
          },
        }}
      >
        <ListSubheader component="div" sx={{ bgcolor: "transparent" }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Apps
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {orgAlias
              ? `Organization: ${orgAlias}`
              : "Select an organization to continue"}
          </Typography>
        </ListSubheader>

        {/* S15: list actual products with styled marks instead of a generic entry. */}
        <ListSubheader component="div" sx={{ bgcolor: "transparent", pt: 1 }}>
          Active products
        </ListSubheader>
        {PRODUCTS.map((product) => {
          const active = activeServices.has(product.serviceAlias);
          return (
            <MenuItem
              key={`${product.serviceAlias}-${product.name}`}
              disabled={!orgAlias}
              onClick={() => handleProduct(product.productKey)}
              // S15: the product mark (aria-hidden) and the secondary status
              // text would otherwise concatenate into the accessible name
              // ("LLM Gateway Available"). Expose just the product name so
              // the menuitem is announced and selectable by its name.
              aria-label={product.name}
            >
              <ListItemIcon>
                <ProductMark active={active} />
              </ListItemIcon>
              <ListItemText
                primary={product.name}
                secondary={
                  orgAlias
                    ? active
                      ? "Active"
                      : "Available"
                    : "Organization required"
                }
              />
            </MenuItem>
          );
        })}

        <Divider />
        <ListSubheader component="div" sx={{ bgcolor: "transparent", pt: 1 }}>
          Browse catalog
        </ListSubheader>
        <MenuItem disabled={!orgAlias} onClick={handlePlatformProducts}>
          <ListItemText primary={PLATFORM_PRODUCTS.CATALOG.title} />
        </MenuItem>

        <ListSubheader component="div" sx={{ bgcolor: "transparent", pt: 1 }}>
          Recently visited
        </ListSubheader>
        <MenuItem onClick={handleOrganization}>
          <ListItemText primary="Organization details" />
        </MenuItem>
        <MenuItem disabled={!orgAlias} onClick={handleProjects}>
          <ListItemText primary="Projects" />
        </MenuItem>
      </Menu>
    </>
  );
};

export default AppsLauncher;
